import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import VehicleSelect from "@/dealership/components/VehicleSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Trash2, Calendar as CalIcon, Clock } from "lucide-react";
import { Stepper, Chip } from "@/components/lux";

const APPT_STEPS = [
  { key: "who", label: "Customer & car" },
  { key: "when", label: "When" },
];

const STATUSES = ["Scheduled", "Confirmed", "Completed", "No-show", "Cancelled"];
const statusStyle = {
  Scheduled: "bg-blue-500/12 text-blue-400 border-blue-500/25",
  Confirmed: "bg-green-500/12 text-green-400 border-green-500/25",
  Completed: "bg-zinc-500/20 text-zinc-400 border-zinc-500/35",
  "No-show": "bg-rose-500/12 text-rose-400 border-rose-500/25",
  Cancelled: "bg-amber-500/12 text-amber-400 border-amber-500/25",
};
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const emptyForm = (date = "") => ({
  lead_id: "", lead_name: "", customer_phone: "", vehicle: "",
  date, time: "10:00", salesperson: "", status: "Scheduled", notes: "",
});

export default function Calendar() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm());

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);

  const { data: appts = [] } = useQuery({
    queryKey: ["appointments", ymd(monthStart), ymd(monthEnd)],
    queryFn: async () =>
      (await api.get("/appointments", { params: { start: ymd(monthStart), end: ymd(monthEnd) } })).data,
  });
  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => (await api.get("/leads")).data,
  });

  const apptsByDay = useMemo(() => {
    const map = {};
    for (const a of appts) (map[a.date] = map[a.date] || []).push(a);
    Object.values(map).forEach((arr) => arr.sort((x, y) => x.time.localeCompare(y.time)));
    return map;
  }, [appts]);

  const cells = useMemo(() => {
    const startPad = monthStart.getDay();
    const total = startPad + monthEnd.getDate();
    const rows = Math.ceil(total / 7) * 7;
    const arr = [];
    for (let i = 0; i < rows; i++) {
      const dayNum = i - startPad + 1;
      arr.push(dayNum >= 1 && dayNum <= monthEnd.getDate() ? new Date(year, month, dayNum) : null);
    }
    return arr;
  }, [year, month]); // eslint-disable-line

  const saveMut = useMutation({
    mutationFn: async (f) => {
      if (editing) return (await api.put(`/appointments/${editing}`, f)).data;
      return (await api.post("/appointments", f)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appointments"] });
      toast.success(editing ? "Appointment updated" : "Test drive booked");
      setOpen(false);
    },
    onError: () => toast.error("Failed to save appointment"),
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/appointments/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appointments"] });
      toast.success("Appointment removed");
      setOpen(false);
    },
  });

  const openNew = (date) => { setEditing(null); setStep(0); setForm(emptyForm(date || ymd(new Date()))); setOpen(true); };
  const openEdit = (a) => { setEditing(a.id); setForm({ ...emptyForm(), ...a }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSelectLead = (id) => {
    const l = leads.find((x) => x.id === id);
    if (l) setForm((f) => ({ ...f, lead_id: id, lead_name: l.name, customer_phone: l.phone || "", vehicle: f.vehicle || l.vehicle_interest || "", salesperson: f.salesperson || l.assigned_to || "" }));
    else set("lead_id", id);
  };

  const todayStr = ymd(new Date());

  return (
    <div className="p-8">
      <PageHeader
        title="Test Drive Calendar"
        subtitle="Schedule and track test-drive appointments with your leads."
        action={
          <Button onClick={() => openNew()} data-testid="add-appointment-button">
            <Plus className="h-4 w-4 mr-2" /> Book Test Drive
          </Button>
        }
      />

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold">{MONTHS[month]} {year}</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border">
          {DAYS.map((d) => (
            <div key={d} className="px-3 py-2 text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const key = d ? ymd(d) : `e${i}`;
            const dayAppts = d ? (apptsByDay[ymd(d)] || []) : [];
            const isToday = d && ymd(d) === todayStr;
            return (
              <div
                key={key}
                data-testid={d ? `cal-day-${ymd(d)}` : undefined}
                onClick={() => d && openNew(ymd(d))}
                className={`min-h-[112px] border-b border-r border-border p-1.5 transition-colors ${d ? "cursor-pointer hover:bg-accent/40" : "bg-secondary/30"} ${i % 7 === 0 ? "border-l" : ""}`}
              >
                {d && (
                  <>
                    <div className={`text-xs font-semibold mb-1 inline-flex items-center justify-center w-6 h-6 rounded-full ${isToday ? "bg-primary text-white" : "text-foreground"}`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {dayAppts.slice(0, 3).map((a) => (
                        <div
                          key={a.id}
                          data-testid={`appt-chip-${a.id}`}
                          onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                          className={`text-[11px] leading-tight px-1.5 py-1 rounded border ${statusStyle[a.status] || "bg-blue-500/12 text-blue-400"} truncate`}
                          title={`${a.time} ${a.lead_name} — ${a.vehicle || ""}`}
                        >
                          <span className="font-mono-data font-semibold">{a.time}</span> {a.lead_name}
                        </div>
                      ))}
                      {dayAppts.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{dayAppts.length - 3} more</div>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalIcon className="h-5 w-5 text-primary" /> {editing ? "Edit Appointment" : "Book Test Drive"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {!editing && (
              <div className="col-span-full mb-2">
                <Stepper steps={APPT_STEPS} current={step} onStep={setStep} testid="appt-stepper" />
              </div>
            )}
            {(editing || step === 0) && (
              <>
                <div className="col-span-full space-y-2">
                  <Label className="label-mono">Lead</Label>
                  <Select value={form.lead_id} onValueChange={onSelectLead}>
                    <SelectTrigger data-testid="appt-lead-select"><SelectValue placeholder="Search & select a lead" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.phone ? ` · ${l.phone}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Customer Name *</Label>
                  <Input value={form.lead_name} onChange={(e) => set("lead_name", e.target.value)} data-testid="appt-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Phone</Label>
                  <Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} />
                </div>
                <div className="col-span-full space-y-2">
                  <Label className="label-mono">Vehicle</Label>
                  <VehicleSelect value={form.vehicle} onChange={(v) => set("vehicle", v)} testId="appt-vehicle-select" placeholder="Select from inventory" />
                </div>
              </>
            )}
            {(editing || step === 1) && (
              <>
                <div className="space-y-2">
                  <Label className="label-mono">Date *</Label>
                  <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="appt-date-input" />
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Time *</Label>
                  <Input type="time" step={300} value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="appt-time-input" />
                </div>
                <div className="col-span-full flex gap-2 flex-wrap">
                  {["10:00", "12:00", "14:00", "16:00", "18:00"].map((t) => (
                    <Chip key={t} active={form.time === t} onClick={() => set("time", t)} testid={`appt-time-${t.replace(":", "")}`}>{t}</Chip>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Salesperson</Label>
                  <Input value={form.salesperson} onChange={(e) => set("salesperson", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label className="label-mono">Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-full space-y-2">
                  <Label className="label-mono">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="justify-between sm:justify-between">
            {editing ? (
              <Button variant="ghost" className="text-destructive" onClick={() => delMut.mutate(editing)} data-testid="appt-delete-button">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => (!editing && step > 0 ? setStep(step - 1) : setOpen(false))}>
                {!editing && step > 0 ? "Back" : "Cancel"}
              </Button>
              {!editing && step === 0 ? (
                <Button data-testid="appt-next" onClick={() => (form.lead_name ? setStep(1) : toast.error("Customer name is required"))}>
                  Continue
                </Button>
              ) : (
                <Button onClick={() => saveMut.mutate(form)} disabled={!form.lead_name || !form.date || !form.time || saveMut.isPending} data-testid="save-appointment-button">
                  {editing ? "Update" : "Book test drive"}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
